import { Outlet, Link, NavLink } from 'react-router-dom';
import { LogOut, CalendarDays, Users, MessageCircle } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { BrandMark } from './BrandMark';

const navPill = ({ isActive }: { isActive: boolean }) =>
  `inline-flex items-center gap-1.5 px-3.5 py-2.5 min-h-touch rounded-full text-[13px] font-semibold cursor-pointer transition-all duration-200 ${
    isActive
      ? 'text-white bg-gradient-to-br from-sage-500 to-sage-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.3),0_4px_10px_-3px_rgba(163,106,122,0.5)]'
      : 'text-ink-mute hover:text-ink hover:bg-white/70'
  }`;

export function Layout() {
  const { account, logout } = useAuth();
  const display = account?.full_name || account?.email || '';
  const isAdmin = account?.account_type === 'admin';

  return (
    <div dir="rtl" className="min-h-full bg-paper">
      <a href="#main" className="skip-link">
        דלג לתוכן
      </a>

      <header className="sticky top-3 z-30 mx-3 sm:mx-6">
        <div className="max-w-5xl mx-auto surface px-4 sm:px-5 h-[4.25rem] flex items-center justify-between gap-3">
          <Link to="/" className="cursor-pointer shrink-0">
            <BrandMark size="sm" showTagline={false} />
          </Link>

          {isAdmin && (
            <nav
              className="hidden md:flex items-center gap-1 flex-1 justify-center"
              aria-label="ניווט ראשי"
            >
              <NavLink to="/" end className={navPill}>
                <CalendarDays size={15} aria-hidden /> חתונות
              </NavLink>
              <NavLink to="/platform/couples" className={navPill}>
                <Users size={15} aria-hidden /> זוגות
              </NavLink>
              <NavLink to="/platform/whatsapp" className={navPill}>
                <MessageCircle size={15} aria-hidden /> WhatsApp
              </NavLink>
            </nav>
          )}

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-semibold text-ink">{display}</span>
              <span className="text-[11px] text-ink-faint font-medium">
                {isAdmin ? 'מנהל מערכת' : 'חשבון זוג'}
              </span>
            </div>
            <button
              type="button"
              onClick={logout}
              className="btn-ghost !px-3"
              aria-label="יציאה מהחשבון"
            >
              <LogOut size={16} strokeWidth={2} aria-hidden />
              <span className="hidden sm:inline">יציאה</span>
            </button>
          </div>
        </div>
      </header>

      {isAdmin && (
        <nav
          className="md:hidden max-w-5xl mx-auto px-4 pt-3 flex gap-1 overflow-x-auto"
          aria-label="ניווט מובייל"
        >
          {[
            { to: '/', end: true, label: 'חתונות' },
            { to: '/platform/couples', label: 'זוגות' },
            { to: '/platform/whatsapp', label: 'WhatsApp' },
          ].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `inline-flex items-center px-3.5 py-2.5 min-h-touch rounded-full text-[12px] font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  isActive
                    ? 'text-white bg-gradient-to-br from-sage-500 to-sage-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.3),0_4px_10px_-3px_rgba(163,106,122,0.5)]'
                    : 'text-ink-mute bg-white/80'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      )}

      <main id="main" className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-20">
        <Outlet />
      </main>
    </div>
  );
}
